import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Upload, FileCheck2, FileX2, Clock, IdCard, ScrollText, Camera, Home as HomeIcon, Loader2, CheckCircle2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  fetchKycStatus, uploadKycDocument, aadhaarStartOtp, aadhaarVerifyOtp, submitBci,
} from "@lawyer/services/kycApi";

const REQUIRED = [
  { key: "aadhaar", label: "Aadhaar (front+back)", icon: IdCard, hint: "PDF or image · we'll redact the number on display" },
  { key: "bci_cert", label: "BCI enrolment certificate", icon: ScrollText, hint: "Scan of your Bar Council enrolment certificate" },
  { key: "degree", label: "Law degree certificate", icon: GraduationCap, hint: "LL.B./J.D./equivalent — issuing university degree certificate" },
  { key: "photo", label: "Recent photograph", icon: Camera, hint: "Headshot, JPG/PNG, used on your profile" },
  { key: "address_proof", label: "Address proof", icon: HomeIcon, hint: "Utility bill / passport / driving license — last 3 months" },
];

const STATUS_PILL = {
  pending_review: { cls: "bg-amber-100 text-amber-800", label: "Under review" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected — re-upload" },
};

export default function LawyerKyc() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try { setData(await fetchKycStatus()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load KYC status"); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onFile = async (docType, file) => {
    if (!file) return;
    setBusy(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await uploadKycDocument(fd);
      toast.success("Uploaded — waiting for review");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="text-slate-500" data-testid="lawyer-kyc-loading">Loading…</div>;

  const docByType = {};
  (data.documents || []).forEach((d) => {
    if (!docByType[d.doc_type] || new Date(d.uploaded_at) > new Date(docByType[d.doc_type].uploaded_at)) {
      docByType[d.doc_type] = d;
    }
  });

  return (
    <>
      <div className="mb-6" data-testid="lawyer-kyc-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          Verification & e-KYC
        </h1>
        <p className="mt-1 text-sm text-slate-500">Verified profiles get 4× more bookings. Upload all 4 documents and complete Aadhaar OKYC.</p>
      </div>

      {/* Progress card */}
      <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="kyc-progress-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Verification status</p>
            <p className="mt-1 text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              {data.verified ? "Verified" : data.verification_status?.replace("_", " ") || "Not started"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{data.progress_pct}%</p>
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Complete</p>
          </div>
        </div>
        <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${data.progress_pct}%` }} />
        </div>
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Required documents</h2>
      <div className="space-y-3 mb-8">
        {REQUIRED.map(({ key, label, icon: Icon, hint }) => {
          const doc = docByType[key];
          const pill = doc ? STATUS_PILL[doc.status] : null;
          return (
            <div key={key} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`kyc-doc-${key}`}>
              <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] text-[#0A2540] grid place-items-center shrink-0">
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold text-[#0A2540]">{label}</p>
                <p className="text-xs text-slate-500">{hint}</p>
                {doc?.review_note && (
                  <p className="mt-1 text-xs text-rose-600">Reviewer note: {doc.review_note}</p>
                )}
              </div>
              {pill && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pill.cls}`} data-testid={`kyc-status-${key}`}>
                  {pill.label}
                </span>
              )}
              <label className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer ${doc?.status === "approved" ? "bg-slate-100 text-slate-500" : "bg-[#0A2540] text-[#D4AF37] hover:opacity-90"}`}>
                {busy === key ? <Loader2 size={14} className="animate-spin inline" /> : <Upload size={12} className="inline mr-1" />}
                {doc ? (doc.status === "approved" ? "Approved" : "Re-upload") : "Upload"}
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid={`kyc-upload-${key}`}
                  onChange={(e) => onFile(key, e.target.files?.[0])}
                  disabled={doc?.status === "approved" || busy === key}
                />
              </label>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <AadhaarOkycCard kycAadhaar={data.kyc_aadhaar} onDone={reload} />
        <BciCard kycBci={data.kyc_bci} onDone={reload} />
      </div>
    </>
  );
}

function AadhaarOkycCard({ kycAadhaar, onDone }) {
  const [aadhaar, setAadhaar] = useState("");
  const [refId, setRefId] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const verified = kycAadhaar?.status === "verified";

  const start = async () => {
    if (!/^\d{12}$/.test(aadhaar.replace(/\D/g, ""))) {
      toast.error("Aadhaar must be exactly 12 digits");
      return;
    }
    setBusy(true);
    try {
      const r = await aadhaarStartOtp(aadhaar.replace(/\D/g, ""));
      if (r.manual_review) {
        toast.success("Submitted for manual review");
        onDone();
      } else {
        setRefId(r.reference_id);
        toast.success("OTP sent to your Aadhaar-linked mobile");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(otp)) { toast.error("OTP must be 6 digits"); return; }
    setBusy(true);
    try {
      const r = await aadhaarVerifyOtp(refId, otp);
      toast.success(`Verified${r.name ? ` — ${r.name}` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="kyc-aadhaar-card">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-[#0A2540]" />
        <p className="font-bold text-[#0A2540]">Aadhaar OKYC</p>
        {verified && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            Verified
          </span>
        )}
      </div>
      {verified ? (
        <div className="mt-3 text-sm text-slate-700">
          <p className="font-bold">{kycAadhaar.name}</p>
          <p className="text-xs text-slate-500">XXXX-XXXX-{kycAadhaar.aadhaar_last4 || "····"}</p>
          <p className="text-xs text-slate-500">DOB: {kycAadhaar.dob}</p>
        </div>
      ) : !refId ? (
        <div className="mt-3 space-y-2">
          <Input
            value={aadhaar}
            onChange={(e) => setAadhaar(e.target.value.replace(/[^\d -]/g, "").slice(0, 14))}
            placeholder="1234 5678 9012"
            inputMode="numeric"
            data-testid="kyc-aadhaar-input"
            className="rounded-xl"
          />
          <Button onClick={start} disabled={busy} data-testid="kyc-aadhaar-start-btn" className="w-full adv-btn-primary rounded-xl h-9">
            {busy && <Loader2 size={14} className="animate-spin mr-1" />} Send OTP
          </Button>
          <p className="text-[11px] text-slate-400">An OTP will be sent to your Aadhaar-registered mobile number.</p>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <Input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit OTP"
            inputMode="numeric"
            data-testid="kyc-aadhaar-otp"
            className="rounded-xl"
          />
          <Button onClick={verify} disabled={busy} data-testid="kyc-aadhaar-verify-btn" className="w-full adv-btn-gold rounded-xl h-9">
            {busy && <Loader2 size={14} className="animate-spin mr-1" />} Verify OTP
          </Button>
          <button className="text-[11px] text-slate-500 hover:text-[#0A2540] underline" onClick={() => { setRefId(null); setOtp(""); }}>
            Use a different number
          </button>
        </div>
      )}
    </div>
  );
}

function BciCard({ kycBci, onDone }) {
  const [enrol, setEnrol] = useState(kycBci?.enrolment_number || "");
  const [name, setName] = useState(kycBci?.name || "");
  const [busy, setBusy] = useState(false);

  const submitted = !!kycBci?.enrolment_number;

  const submit = async () => {
    if (!enrol.trim()) { toast.error("Enrolment number required"); return; }
    setBusy(true);
    try {
      await submitBci(enrol.trim(), name.trim());
      toast.success("Submitted for verification");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="kyc-bci-card">
      <div className="flex items-center gap-2">
        <ScrollText size={16} className="text-[#0A2540]" />
        <p className="font-bold text-[#0A2540]">Bar Council enrolment</p>
        {kycBci?.status === "verified" && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            Verified
          </span>
        )}
        {kycBci?.status === "manual_review" && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            Under review
          </span>
        )}
      </div>
      <div className="mt-3 space-y-2">
        <Input
          value={enrol} onChange={(e) => setEnrol(e.target.value)}
          placeholder="MAH/12345/2010"
          disabled={submitted}
          data-testid="kyc-bci-enrol"
          className="rounded-xl"
        />
        <Input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Name as on certificate"
          disabled={submitted}
          data-testid="kyc-bci-name"
          className="rounded-xl"
        />
        {!submitted && (
          <Button onClick={submit} disabled={busy} data-testid="kyc-bci-submit-btn" className="w-full adv-btn-primary rounded-xl h-9">
            {busy && <Loader2 size={14} className="animate-spin mr-1" />} Submit for review
          </Button>
        )}
        {submitted && (
          <p className="text-xs text-slate-500">Submitted on {kycBci?.submitted_at?.slice(0, 10)}. Our team will verify with the State Bar Council in 1-2 business days.</p>
        )}
      </div>
    </div>
  );
}
